<template>
    <v-timeline-item :color="_.has(item, 'color') ? item.color : 'background'" :icon="getIcon(item.icon)">
        <template #icon>
            <app-button
                v-if="isGrowthInvest() && isTodo && !readonly"
                color="success"
                :icon-color="todoChecked ? 'success' : 'grey'"
                :icon="getIcon('todoSuccess', 'fad')"
                icon-size="1.4rem"
                class="app-timeline-item__todo"
                :loading="todoLoading || awaitingTodoSave"
                @click="onTodoToggle"
            />
            <v-icon
                v-else-if="item.icon"
                size="1.4rem"
                :color="item.icon.toLowerCase() === 'success' ? 'success' : 'primaryText'"
            >
                {{ getIcon(item.icon, 'fad') }}
            </v-icon>
        </template>

        <div class="app-timeline-date caption">
            <display-date :value="item.date" class="text-caption" />
        </div>

        <p class="app-timeline-title text-h6">
            <span>{{ item.title }}</span>
            <app-button
                v-if="canEdit && !editing"
                small
                icon="edit"
                :tooltip="$t('edit_this_item')"
                @click="editing = true"
            />
        </p>

        <transition appear name="fade" mode="out-in">
            <edit-paragraph v-if="canEdit && editing" ref="text" v-model="item.text" :disabled="saving" class="mb-2" />
            <template v-else>
                <p v-if="item.text" class="app-timeline-text">
                    <span>{{ item.text }}</span>
                </p>
            </template>
        </transition>
        <transition appear name="fade" mode="out-in">
            <div v-if="canEdit && editing">
                <app-button :loading="saving" :text="$t('save')" icon="save" icon-prefix="fas" @click="onSave" />
                <app-button
                    class="ml-2"
                    secondary
                    :disabled="saving"
                    :text="$t('cancel')"
                    @click="
                        editing = false;
                        $emit('cancel-editing');
                    "
                />
            </div>
        </transition>

        <div v-if="alertItems && alertItems.length > 0" class="app-timeline-alerts">
            <app-chip
                v-for="(alert, alertIndex) in alertItems"
                :key="alertIndex"
                :type="alert.type"
                :icon="getIcon(alert.icon)"
                :tooltip="alert.prefix"
                :text="alert.text"
                large
                clickable
                @click="$emit('click:alert', { alert, event: item })"
            />
        </div>
    </v-timeline-item>
</template>

<script>
import { Activity } from '@/models';
import snack from '@/lib/helpers/snack';

export default {
    name: 'AppTimelineItem',
    props: {
        event: {
            type: Object,
            required: true
        },
        readonly: {
            type: Boolean,
            default: false
        },
        editable: {
            type: Boolean,
            default: false
        },
        awaitingTodoSave: {
            type: Boolean,
            default: false
        }
    },
    data: () => ({
        todoLoading: false,
        editing: false,
        saving: false,
        item: new Activity()
    }),
    computed: {
        isTodo() {
            return 'todo' in this.item && typeof this.item.todo === 'boolean';
        },
        todoChecked() {
            if (!this.isTodo) return null;
            return !this.item.todo;
        },
        canEdit() {
            if (!this.item) {
                return false;
            }

            if (this.item.title === this.$t('proposed_amounts')) {
                return this.editable;
            }

            if (this.item.title === this.$t('note')) {
                return this.editable;
            }

            return false;
        },
        alertItems() {
            return this.item.alerts;
        }
    },
    watch: {
        event: {
            immediate: true,
            deep: true,
            handler() {
                this.resetForm();
            }
        },
        editing(v) {
            if (!v) {
                this.resetForm();
            }
        }
    },
    methods: {
        async onTodoToggle() {
            this.todoLoading = true;

            try {
                const todo = (
                    await Activity.api.patch(this.item.id, {
                        todo: this.todoChecked
                    })
                ).data;

                this.item.todo = this.todoChecked;

                this.$emit('input:todo', todo);
            } catch (error) {
                this.handleError(error, () => this.onTodoToggle());
            }

            this.todoLoading = false;
        },
        resetForm() {
            if (!this.event) {
                this.item = new Activity();
            } else if (this.event instanceof Activity) {
                this.item = this.event;
            } else {
                this.item = new Activity(this.event);
            }

            this.editing = this.item.$editing;
        },
        async onSave() {
            if (!this.canEdit) {
                return;
            }

            this.saving = true;

            try {
                let response = null;
                if (this.item.id) {
                    response = await this.item.$update({ text: this.item.text });

                    snack({
                        timeout: 20000,
                        type: 'success',
                        text: `${this.$t('activity')} ${this.$t('updated')}`
                    });
                } else {
                    response = await this.item.$create();

                    snack({
                        timeout: 20000,
                        type: 'success',
                        text: `${this.$t('activity')} ${this.$t('created')}`
                    });
                }

                this.item.$editing = false;
                this.editing = false;
                this.$emit('saved', response);
            } catch (error) {
                this.handleError(error, this.onSave);
            }

            this.saving = false;
        }
    }
};
</script>
